import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import Interceptor from "../../services/Interceptor";
import { SPACING } from "../../theme/constants";

const AdminWorkers = () => {
  const [workers, setWorkers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        const [workersRes, statsRes] = await Promise.all([
          Interceptor.get("/workers"),
          Interceptor.get("/workers/stats"),
        ]);
        setWorkers(workersRes.data?.workers || []);
        setStats(statsRes.data?.stats || null);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch workers");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "success",
      OFFLINE: "error",
      DRAINING: "warning",
      MAINTENANCE: "info",
    };
    return colors[status] || "default";
  };

  return (
    <Box>
      <Box sx={{ mb: SPACING.lg }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
          Worker Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor and manage distributed worker nodes
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">
                Total Workers
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {stats.total || 0}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">
                Online
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {stats.active || 0}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 150 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary">
                Offline
              </Typography>
              <Typography variant="h5" fontWeight={700} color="error.main">
                {stats.offline || 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Workers Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Hostname / IP</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sites</TableCell>
                    <TableCell>Last Heartbeat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No workers registered
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    workers.map((worker) => (
                      <TableRow key={worker.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {worker.name || worker.id?.substring(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {worker.hostname || worker.ipAddress || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={worker.status || "unknown"}
                            size="small"
                            color={getStatusColor(worker.status)}
                          />
                        </TableCell>
                        <TableCell>{worker.currentSites || 0}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {worker.lastHeartbeat
                              ? new Date(worker.lastHeartbeat).toLocaleString()
                              : "Never"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AdminWorkers;
